/**
 * Popup — generic popup wrapper. Listens (delegated on document.body) for
 * `[data-action="toggle-popup"]` clicks, ESC keydown, `drawer:close-other`
 * and `edit-img:open-popup` events. Maintains a stack of nested popups,
 * handles overlay clicks, body scroll lock, focus trap, transition cleanup.
 *
 * Loaded as ES module via `@theme/popup` importmap entry.
 * Exports both the class and a pre-built `globalPopup` singleton.
 * Module is loaded globally from `layout/theme.liquid` so the singleton
 * registers its document-level listeners on every page.
 *
 * Dependencies:
 *   - @theme/delegate
 *   - @theme/body-scroll-lock-options
 *   - @theme/dynamic-variables
 *   - @theme/body-scroll-lock
 *   - @theme/focus-trap
 */

import { Delegate } from '@theme/delegate';
import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { dynamicVars } from '@theme/dynamic-variables';
import { trapFocus, removeTrapFocus } from '@theme/focus-trap';
import { disableBodyScroll, enableBodyScroll } from '@theme/body-scroll-lock';

export class Popup {
  constructor() {
    this.delegateBody = new Delegate(document.body);
    this.activePopups = [];
    this.onPopupOpen = null;
    this.onPopupClose = null;
    this.bindEvents();
  }

  bindEvents() {
    this.delegateBody.on(
      'click',
      '[data-action="toggle-popup"]',
      this.handleOpenOptions.bind(this)
    );
    this.delegateBody.on(
      'drawer:close-other',
      this.handleDrawerCloseOther.bind(this)
    );
    this.delegateBody.on(
      'edit-img:open-popup',
      this.handleOpenOptions.bind(this)
    );
    this.delegateBody.on('keydown', this.handleEscape.bind(this));
  }

  handleOpenOptions(event) {
    event.stopPropagation();
    event.preventDefault();

    dynamicVars.syncHeight();
    const toggleButton = event.target.closest('[data-action="toggle-popup"]');
    if (toggleButton) {
      this.togglePopup(toggleButton);
      return;
    }

    if (this.activePopups.length) {
      const topPopup = this.activePopups[this.activePopups.length - 1];
      const overlay = this.getOverlayElement(topPopup);
      if (overlay && overlay.contains(event.target)) {
        this.closePopup(topPopup);
      }
    }
  }

  handleEscape(event) {
    if (event.key !== 'Escape' || !this.activePopups.length) return;

    event.preventDefault();
    event.stopPropagation();
    const topPopup = this.activePopups[this.activePopups.length - 1];
    const toggleBtn = topPopup.querySelector('[data-action="toggle-popup"]');
    if (toggleBtn) {
      toggleBtn.click();
    } else {
      this.closePopup(topPopup);
    }
  }

  togglePopup(button) {
    const popupId = button.getAttribute('aria-controls');
    if (!popupId) return;

    const popupElement = document.getElementById(popupId);
    if (!popupElement) return;

    const isChild = button.getAttribute('data-child-popup') === 'true';

    if (popupElement.getAttribute('aria-hidden') === 'false') {
      this.closePopup(popupElement);
    } else {
      // Pass the toggle button as the opener so focus returns to it on close — robust across
      // browsers (Safari doesn't focus a <button> on click, so activeElement would be <body>).
      this.openPopup(popupElement, isChild, button);
    }
  }

  openPopup(popupElement, isChild = false, opener = document.activeElement) {
    popupElement._isChild = isChild;
    // Remember what to focus when this popup closes (the trigger that opened it). Captured now
    // because document.activeElement drifts once we hide/inert the popup and remove the trap.
    popupElement._returnFocusTo = opener instanceof HTMLElement ? opener : null;

    if (!isChild) {
      while (this.activePopups.length) {
        const activePopup = this.activePopups.pop();
        this._closePopup(activePopup);
        this._releaseScrollLock(activePopup);
      }
    }

    // Show instantly (no transition).
    popupElement.style.display = '';
    popupElement.removeAttribute('inert');
    popupElement.setAttribute('aria-hidden', 'false');
    popupElement.setAttribute('aria-expanded', 'true');
    if (typeof this.onPopupOpen === 'function') {
      this.onPopupOpen(popupElement);
    }

    const focus = popupElement.matches('[data-scrollable]')
      ? popupElement
      : popupElement.querySelector('[data-scrollable]');
    if (focus) {
      popupElement._scrollableElement = focus;
      disableBodyScroll(focus, bodyScrollLockOptions);
    }

    trapFocus(popupElement);

    const overlay = this.getOverlayElement(popupElement);
    if (overlay) {
      overlay.classList.add('is-visible', 'is-popup-overlay');
      const handleOverlayClick = (e) => {
        e.preventDefault();
        this.closePopup(popupElement);
      };
      popupElement._overlayClickHandler = handleOverlayClick;
      overlay.addEventListener('click', handleOverlayClick);
    }

    this.activePopups.push(popupElement);
    // Single open signal for every path (toggle button, programmatic open, deep link);
    // feature modules listen on their own popup element.
    popupElement.dispatchEvent(
      new CustomEvent('popup:opened', {
        bubbles: true,
        detail: { opener: popupElement._returnFocusTo, isChild },
      })
    );
  }

  closePopup(popupElement) {
    const index = this.activePopups.indexOf(popupElement);
    if (index === -1) return;

    this._closePopup(popupElement);
    this.activePopups.splice(index, 1);
    this._releaseScrollLock(popupElement);

    if (this.activePopups.length > 0) {
      const topPopup = this.activePopups[this.activePopups.length - 1];
      if (topPopup._scrollableElement) {
        // On iOS, enableBodyScroll restores the body position even while another
        // target remains registered. Re-arm the revealed popup so its lock owns
        // the current fixed-body state after a child popup closes.
        enableBodyScroll(topPopup._scrollableElement);
        disableBodyScroll(topPopup._scrollableElement, bodyScrollLockOptions);
      }
    }
  }

  _releaseScrollLock(popupElement) {
    const scrollableElement = popupElement._scrollableElement;
    if (!scrollableElement) return;

    enableBodyScroll(scrollableElement);
    delete popupElement._scrollableElement;
  }

  _closePopup(popupElement) {
    // Hide instantly (no transition).
    popupElement.setAttribute('aria-hidden', 'true');
    popupElement.setAttribute('aria-expanded', 'false');
    popupElement.setAttribute('inert', '');
    popupElement.style.display = 'none';

    const overlay = this.getOverlayElement(popupElement);
    if (overlay) {
      overlay.classList.remove('is-visible', 'is-popup-overlay');
      if (popupElement._overlayClickHandler) {
        overlay.removeEventListener('click', popupElement._overlayClickHandler);
        delete popupElement._overlayClickHandler;
      }
    }

    // Drop the trap WITHOUT focusing the popup (it's now hidden + inert — focusing it would
    // lose focus to <body>). Then return focus to the opener, so keyboard users land back on
    // the trigger they came from (e.g. the look card in a grid).
    const returnFocusTo = popupElement._returnFocusTo;
    delete popupElement._returnFocusTo;
    removeTrapFocus();
    if (returnFocusTo instanceof HTMLElement && returnFocusTo.isConnected && returnFocusTo !== document.body) {
      returnFocusTo.focus({ preventScroll: true });
    }

    if (typeof this.onPopupClose === 'function') {
      this.onPopupClose(popupElement);
    }
  }

  handleDrawerCloseOther(event) {
    event.preventDefault();
    this.closeAllPopups();
  }

  closeAllPopups() {
    while (this.activePopups.length) {
      const activePopup = this.activePopups.pop();
      this._closePopup(activePopup);
      this._releaseScrollLock(activePopup);
    }
  }

  getOverlayElement(popupElement) {
    const overlayClass = popupElement.getAttribute('data-overlay');
    if (!overlayClass) return null;
    return document.querySelector(`.${overlayClass}`);
  }
}

export const globalPopup = new Popup();
window.globalPopup = globalPopup;
